/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import '../../utils/device-identifier.js'; // just to pull in modrnzr
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { KEYCODE } from '../../utils/keycode.js';
/**
 * @prop {Boolean} collapsed - Default false. Collapses the tab selection into a dropdown menu. Automatically triggered when tabs are used in the context of an Access Bar and the screen is smaller than 1024.
 * @prop {Boolean} active - Default false. If collapsed is set to true, this property defines if the dropdown menu is open or not.
 * @prop {String} label - If collapsed is set to true, this property defines the label shown at a fixed position. The value of this property is updated on load and every time a new tab-item is selected.
 * @prop {String} icon - If collapsed is set to true, this property defines the icon shown at a fixed position. The value of this property is updated on load and every time a new tab-item is selected.
 * @prop {String} info - If collapsed is set to true and the active tab-item is a patient item, this property defines the info shown below the label. The value of this property is updated on load and every time a new tab-item is selected.
 * @prop {Boolean} fixed - Default false. If set to `true`, the tabs will not be collapsed when placed inside the Access Bar for small screen sizes.
 * @prop {Boolean} condensed - Adds condensed styles if set to `true`.
 * @prop {Boolean} scrollable - If set to `true`, the tabs will scroll horizontally when there are too many tabs across the viewport.
 * @prop {Boolean} noArrows - If `scrollable` is set to true, this property can be used in a touch environment, where scrolling horizontally is intuitive and the arrows can be dismissed due to lack of space.
 * @prop {Boolean} noScroll - If `no-scroll` is set to true, the tabs will not scroll horizontally when there are too many tabs across the viewport.
 * @attr {Boolean} no-arrows - If `scrollable` is set to true, this property can be used in a touch environment, where scrolling horizontally is intuitive and the arrows can be dismissed due to lack of space.
 * @slot Holds the array of tab-items that will be shown as options. Takes sh-tab-item as children.
 */

export class SHTabs extends FocusBlurMixin(LitElement) {
  static get properties() {
    return {
      /** @ignore */
      role: { type: String, reflect: true },
      collapsed: { type: Boolean, reflect: true },
      active: { type: Boolean, reflect: true },
      label: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      info: { type: String, reflect: true },
      /** @ignore */
      _accessContext: {
        type: Boolean,
        reflect: true,
        attribute: 'access-context',
      },
      /** @ignore */
      _navContext: { type: Boolean, reflect: true, attribute: 'nav-context' },
      /** @ignore */
      _footerContext: {
        type: Boolean,
        reflect: true,
        attribute: 'footer-context',
      },
      condensed: { type: Boolean, reflect: true },
      fixed: { type: Boolean, reflect: true },
      scrollable: { type: Boolean, reflect: true },
      noArrows: { type: Boolean, reflect: true, attribute: 'no-arrows' },
      noScroll: { type: Boolean, reflect: true, attribute: 'no-scroll' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: relative;
          display: flex;
          flex-direction: row;
          overflow: hidden;
          border-bottom: 0.0625rem solid rgba(var(--ui-1), var(--opacity-7));
        }
        :host(.item-focused) {
          outline: var(--focus-outline);
          outline-offset: -0.125rem;
        }
        :host([access-context]),
        :host([nav-context]),
        :host([footer-context]) {
          border-bottom: none;
        }

        /* collapsed */

        .collapsed-popover {
          position: absolute;
          top: 100%;
          left: 0rem;
          background: var(--base-3);
          box-shadow: var(--shadow-overlay);
          border-radius: 0.125rem;
          min-width: 100%;
          max-width: 15rem;
          max-height: 0rem;
          opacity: 0;
          transform: translateY(-1.5rem);
          overflow: hidden;
          display: none;
          transition: var(--transition-time) all ease-in-out;
        }
        :host([collapsed][active]) .collapsed-popover {
          overflow: auto;
          max-height: 12.5rem;
          opacity: 1;
          transform: none;
          display: block;
        }
        :host([collapsed][active]) #expand-icon {
          transform: rotate(180deg);
        }
        :host([collapsed]) {
          overflow: visible;
        }
        :host([collapsed]) .collapsed-input {
          cursor: pointer;
          display: inline-flex;
          height: 4.5rem;
          min-width: 6.25rem;
          max-width: 25.5rem;
          padding: 1.25rem 0.5rem;
          box-sizing: border-box;
          font: var(--body-1);
          color: var(--text-primary);
          line-height: 2rem;
        }

        :host([collapsed][condensed]) .collapsed-input {
          height: 2.5rem;
          padding: 0.25rem 0.5rem;
        }

        :host([collapsed][condensed]) .text {
          line-height: 2rem;
        }

        :host([collapsed][condensed]) .info {
          display: none;
        }

        #expand-icon {
          transition: 0s transform, var(--transition-time) color ease-in-out;
          margin-left: 0.5rem;
          color: rgba(var(--ui-1), var(--opacity-4));
        }

        #tab-icon {
          color: rgba(var(--ui-1), var(--opacity-2));
          margin-right: 0.5rem;
        }

        :host([info]) .text {
          font: var(--title-1);
          line-height: 1rem;
        }

        .text {
          color: var(--text-primary);
          flex-direction: column;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .info {
          font: var(--body-2);
          color: var(--text-secondary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .items-scroll-wrapper {
          display: flex;
          width: fit-content;
          width: -moz-fit-content;
        }
        .items-wrapper {
          overflow-x: scroll;
          overflow-y: hidden;
          scrollbar-width: none;
          scroll-behavior: smooth;
          scroll-snap-type: x proximity;
        }
        .arrow-icons {
          padding: 0.75rem 0.5rem;
        }
        :host([condensed]) .arrow-icons {
          padding: 0.25rem 0;
        }
        :host([access-context]:not([condensed])) .arrow-icons {
          padding: 1.25rem 0;
        }
        ::-webkit-scrollbar {
          height: 0rem;
        }
        .hidden,
        :host([no-arrows]) .arrow-icons {
          display: none;
        }
        /* hover */
        :host([collapsed]:not(.no-hovermq)) .collapsed-input:hover #expand-icon {
          color: rgba(var(--ui-1), var(--opacity-3));
        }
      `,
    ];
  }

  render() {
    return html`
      ${this.collapsed
        ? html` <div class="collapsed-input" @click="${() => this._show()}">
              <sh-icon icon="${this.icon}" id="tab-icon"></sh-icon>
              <div class="text">
                ${this.label}
                <div class="info">${this.info}</div>
              </div>
              <div class="info"></div>
              <sh-icon icon="down-s" id="expand-icon"></sh-icon>
            </div>
            <div class="collapsed-popover">
              <slot id="collapsed-slot" @slotchange="${this._handleTabsSlotChange}"></slot>
            </div>`
        : html` ${this.scrollable && !this.noScroll
            ? html`<sh-icon
                icon="arrow-line-left-s"
                button
                @click="${() => this.scrollTabs('left')}"
                class="arrow-icons left hidden"
              ></sh-icon>`
            : ''}
          ${this.noScroll
            ? html` <slot id="main-slot" @slotchange="${this._handleTabsSlotChange}"></slot>`
            : html` <div class="items-wrapper" @scroll="${this._handleArrows.bind(this)}">
                <div class="items-scroll-wrapper">
                  <slot id="main-slot" @slotchange="${this._handleTabsSlotChange}"></slot>
                </div>
              </div>`}
          ${this.scrollable && !this.noScroll
            ? html`<sh-icon
                icon="arrow-line-right-s"
                button
                @click="${() => this.scrollTabs('right')}"
                class="arrow-icons right"
              ></sh-icon>`
            : ''}`}
    `;
  }

  constructor() {
    super();
    this.fixed = false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.role = 'navigation';
    }
    if (!Modernizr.hovermq) {
      this.classList.add('no-hovermq');
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.collapsed) return;
      const itemsScrollWrapper = this.shadowRoot.querySelector('.items-scroll-wrapper');
      if (!itemsScrollWrapper) return;
      if (itemsScrollWrapper.getBoundingClientRect().width > this.getBoundingClientRect().width) {
        this._handleOverflow(true);
      } else {
        this._handleOverflow(false);
      }
    });
    this.resizeObserver.observe(this);

    // update the tab elements active state
    this.addEventListener('clicked', function (e) {
      this.label = e.target.label;
      this.icon = e.target.icon;

      if (e.target.patient === true) {
        this.icon = 'patient';
        this.info = e.target.info;
      } else {
        this.info = null;
      }
      // get name for tabs without labels
      if (e.target.label === undefined) {
        this.label = e.target.name;
      }
      this._makeTabItemsInactive();
      this._hide();
    });
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  firstUpdated() {
    super.firstUpdated();
    if (this.condensed) {
      this.setAttribute('tabindex', '0');
    } else {
      this.removeAttribute('tabindex');
    }
    // close popover on focusout
    this.addEventListener('focusout', function (event) {
      // If focus is still in the popover, do nothing
      if (this.contains(event.relatedTarget)) return;
      this.active = false;
    });
  }

  updated(changedProperties) {
    if (changedProperties.has('collapsed')) {
      this._collapsedChanged();
    }
    if (changedProperties.has('condensed')) {
      this._condensedChanged();
    }
    const listOfProperties = [
      'role',
      'collapsed',
      'active',
      'label',
      'icon',
      'info',
      'condensed',
      'fixed',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  scrollTabs(direction) {
    const itemsWrapper = this.shadowRoot.querySelector('.items-wrapper');
    if (direction === 'left') {
      itemsWrapper.scrollLeft -= 100;
    } else {
      itemsWrapper.scrollLeft += 100;
    }
  }

  _handleOverflow(isOverflowing) {
    this.scrollable = isOverflowing;
  }

  _handleArrows() {
    const itemsWrapper = this.shadowRoot.querySelector('.items-wrapper');
    if (!itemsWrapper) return;
    const leftIcon = this.shadowRoot.querySelector('sh-icon.left');
    const rightIcon = this.shadowRoot.querySelector('sh-icon.right');
    if (leftIcon) {
      if (itemsWrapper.scrollLeft == 0) {
        leftIcon.classList.add('hidden');
      } else {
        leftIcon.classList.remove('hidden');
      }
    }
    if (rightIcon) {
      if (
        Math.ceil(itemsWrapper.scrollLeft + itemsWrapper.offsetWidth) >= itemsWrapper.scrollWidth
      ) {
        rightIcon.classList.add('hidden');
      } else {
        rightIcon.classList.remove('hidden');
      }
    }
  }

  _makeTabItemsInactive() {
    const children = this.querySelectorAll('sh-tab-item[active]');

    for (let i = 0; i < children.length; i++) {
      children[i].active = false;
    }
  }

  _setTabsProperties() {
    const activeTab = this.querySelector('[active]');
    if (activeTab === null) return;

    // populate placeholder properties with data from active tab-item
    if (activeTab.attributes.name !== undefined) {
      this.label = activeTab.attributes.name.value;
    } else if (activeTab.attributes.label !== undefined) {
      this.label = activeTab.attributes.label.value;
    }
    if (activeTab.attributes.patient !== undefined) {
      this.icon = 'patient';
      if (activeTab.attributes.info !== undefined) {
        this.info = activeTab.attributes.info.value;
      }
    } else if (activeTab.attributes.icon !== undefined) {
      this.icon = activeTab.attributes.icon.value;
    }
  }

  _condensedChanged() {
    const tabItems = this.querySelectorAll('sh-tab-item');

    for (let i = 0; i < tabItems.length; i++) {
      tabItems[i].condensed = this.condensed;
    }
  }

  _collapsedChanged() {
    const tabItems = this.querySelectorAll('sh-tab-item');

    for (let i = 0; i < tabItems.length; i++) {
      tabItems[i].collapsed = this.collapsed;
    }
    if (this.collapsed) {
      this.setAttribute('tabindex', '1');
    } else {
      this.removeAttribute('tabindex');
    }
  }

  _show() {
    if (this.active === true) {
      this._hide();
      return;
    }
    this.active = true;
  }

  _hide() {
    this.active = false;
  }
  _handleTabsSlotChange() {
    const tabItem = this.querySelectorAll('sh-tab-item');
    if (this._accessContext) {
      tabItem.forEach((el) => {
        el._accessContext = true;
      });
    }
    this._setTabsProperties();
    this._collapsedChanged();
    this._condensedChanged();
  }
  handleKeyup(e) {
    super.handleKeyup(e);
    if (e.keyCode === KEYCODE.SPACE || e.keyCode === KEYCODE.ENTER) {
      this.toggleAttribute('active');
    }
    // arrow_down on collapsed active sh-tabs moves focus to first possible child
    if (e.keyCode === KEYCODE.ARROW_DOWN && this.collapsed && this.active) {
      let fChild = this.querySelector('SH-TAB-ITEM:not([disabled])');
      fChild.focus();
      fChild.classList.add('item-focused');
    }
    // arrow_up on collapsed active sh-tabs moves focus to last possible child
    if (e.keyCode === KEYCODE.ARROW_UP && this.collapsed && this.active) {
      let child = this.querySelectorAll('SH-TAB-ITEM:not([disabled])');
      let lChild = child[child.length - 1];
      lChild.focus();
      lChild.classList.add('item-focused');
    }
  }
}

customElements.define('sh-tabs', SHTabs);
